import React, { useCallback, useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
  adminDangerButton,
} from "../components/AdminPageShell";
import { useConfirmDialog } from "../components/ConfirmDialogProvider";
import { useStaffDepartments } from "../store/staff";

const EMPTY_FORM = { name: "", faculty: "" };
const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");

export default function StaffDepartments() {
  const confirm = useConfirmDialog();
  const {
    staffDepartments,
    staffDepartmentsLoading,
    staffDepartmentsError,
    staffDepartmentMutationLoading,
    staffDepartmentMutationError,
    fetchStaffDepartments,
    createStaffDepartment,
    updateStaffDepartment,
    deleteStaffDepartment,
  } = useStaffDepartments();

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");

  const showMessage = useCallback((text) => {
    setMessage(text);
    setTimeout(() => setMessage(""), 5000);
  }, []);

  useEffect(() => {
    fetchStaffDepartments();
  }, [fetchStaffDepartments]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (department) => {
    setEditingId(department.id);
    setForm({ name: department.name || "", faculty: department.faculty || "" });
    setShowForm(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = { name: form.name.trim() };
    if (!payload.name) return;
    payload.faculty = form.faculty.trim() || null;

    const result = editingId
      ? await updateStaffDepartment(editingId, payload)
      : await createStaffDepartment(payload);
    if (!result.success) return;

    showMessage(editingId ? "Department updated." : "Department created.");
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    await fetchStaffDepartments();
  };

  const handleDelete = (department) =>
    confirm({
      title: `Delete "${department.name}"?`,
      description: "This action cannot be undone.",
      confirmLabel: "Delete department",
      onConfirm: async () => {
        const result = await deleteStaffDepartment(department.id);
        if (!result.success) throw new Error(result.error || "Failed to delete department.");
        showMessage("Department deleted.");
        await fetchStaffDepartments();
      },
    });

  return (
    <div>
      <StaffNavBar
        title="Departments"
        context="Manage the departments used to organise academic resources."
      />
      <AdminPageBody>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="m-0 text-lg font-semibold text-slate-900">Departments</h2>
            <p className="m-0 mt-1 text-sm text-slate-500">
              {staffDepartments.length} department{staffDepartments.length === 1 ? "" : "s"}
            </p>
          </div>
          <button className={adminPrimaryButton} onClick={openCreate}>
            Add department
          </button>
        </div>

        {message && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {message}
          </div>
        )}
        {staffDepartmentMutationError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {staffDepartmentMutationError}
          </div>
        )}

        {showForm && (
          <AdminPanel className="p-5">
            <form className="grid gap-3 sm:grid-cols-3" onSubmit={handleSubmit}>
              <label className="text-xs font-semibold text-slate-600">
                Name
                <input
                  className={`${inputClass} mt-1`}
                  required
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="text-xs font-semibold text-slate-600">
                Faculty
                <input
                  className={`${inputClass} mt-1`}
                  value={form.faculty}
                  onChange={(event) => setForm({ ...form, faculty: event.target.value })}
                />
              </label>
              <div className="flex items-end gap-2">
                <button className={adminPrimaryButton} disabled={staffDepartmentMutationLoading}>
                  {staffDepartmentMutationLoading
                    ? "Saving…"
                    : editingId
                    ? "Save changes"
                    : "Create"}
                </button>
                <button
                  type="button"
                  className={adminButton}
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    setForm(EMPTY_FORM);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </AdminPanel>
        )}

        <AdminPanel className="overflow-hidden">
          {staffDepartmentsError && (
            <p className="px-4 text-sm text-red-700">{staffDepartmentsError}</p>
          )}
          {staffDepartmentsLoading ? (
            <AdminLoadingState />
          ) : staffDepartments.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-500">
              No departments found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Name</th>
                    <th className="px-5 py-3 font-semibold">Slug</th>
                    <th className="px-5 py-3 font-semibold">Faculty</th>
                    <th className="px-5 py-3 font-semibold">Resources</th>
                    <th className="px-5 py-3 font-semibold">Created</th>
                    <th className="px-5 py-3 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staffDepartments.map((department) => (
                    <tr key={department.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-medium text-slate-900">
                        {department.name}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-500">
                        {department.slug || "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {department.faculty || "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {department.resourceCount ?? 0}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">
                        {fmtDate(department.createdAt)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-center gap-2">
                          <button className={adminButton} onClick={() => openEdit(department)}>
                            Edit
                          </button>
                          <button
                            className={adminDangerButton}
                            disabled={staffDepartmentMutationLoading}
                            onClick={() => handleDelete(department)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminPanel>
      </AdminPageBody>
    </div>
  );
}
